import {
    Component,
    ComponentInterface,
    h,
    Prop,
    Host,
    Event,
    EventEmitter,
    Method,
    Element,
    State,
} from "@stencil/core";
import { hasSlot } from "../../utils/utils";

let id = 0;

/**
 * @slot label - The label above the textarea.
 * @slot flag - The flag shown for invalid input.
 * @slot helper-text - The text beneath the textarea.
 */
@Component({
    tag: "uic-multiline-input",
    styleUrl: "multiline-input.scss",
})
export class MultilineInput implements ComponentInterface {
    private textarea: HTMLTextAreaElement;
    private labelId: string;
    private inputId: string;
    private hasFlagSlot: boolean;

    @State() hasFocus = false;

    @Element() host: HTMLUicMultilineInputElement;

    /**
     * The autocomplete attribute of textarea.
     */
    @Prop() autocomplete: string;

    /**
     * If set to `true`, the user cannot interact with component.
     */
    @Prop() disabled = false;

    /**
     * Information with low emphasis.
     */
    @Prop() helperText: string;

    /**
     * If set to `true`, the value is considered incorrect.
     * An invalid state will show the error flag.
     */
    @Prop() invalid: boolean = false;

    /**
     * error message to show instead of helper text if `invalid` is set
     */
    @Prop() invalidText: string;

    /**
     * The label.
     */
    @Prop() label: string;

    /**
     * The name of the control, which is submitted with the form data.
     */
    @Prop() name: string;

    /**
     * The minmum number of characters.
     */
    @Prop() minlength: number;

    /**
     * The maximum number of characters.
     */
    @Prop() maxlength: number;

    /**
     * The value of the textarea.
     */
    @Prop({ mutable: true, reflect: true }) value = "";

    /**
     * A hint to the user of what can be entered in the control.
     */
    @Prop() placeholder: string;

    /**
     * Indicates that the user cannot modify the value of the control.
     */
    @Prop() readonly: boolean;

    /**
     * If `true`, the element will have its spelling and grammar checked.
     */
    @Prop() spellcheck: boolean = false;

    /**
     * Controls if field is marked as requiring some input in case user has
     * not entered anything.
     */
    @Prop() required: boolean = false;

    /**
     * Controls if field is marked as having changed since last save. This is
     * about some visual indicator, only. The actual saving is beyond the scope
     * of this component.
     */
    @Prop() unsaved: boolean = false;

    /**
     * Controls number of rows to display.
     */
    @Prop() rows: number = 2;

    /**
     * Emitted when the value is committed.
     */
    @Event() uicChange: EventEmitter;

    /**
     * Emitted when the value changes.
     */
    @Event() uicInput: EventEmitter;

    /**
     * Emitted when the control loses focus.
     */
    @Event() uicBlur: EventEmitter;

    /**
     * Emitted when the control gets focus.
     */
    @Event() uicFocus: EventEmitter;

    componentWillLoad() {
        this.inputId = `uic-multiline-input-textarea-${++id}`;
        this.labelId = `uic-multiline-input-label-${id}`;
        this.hasFlagSlot = !!this.host.querySelector('[slot="flag"]');
    }

    /**
     * Sets focus to the textarea.
     */
    @Method()
    async setFocus(options?: FocusOptions) {
        this.textarea.focus(options);
    }

    /**
     * Removes focus from the textarea.
     */
    @Method()
    async removeFocus() {
        this.textarea.blur();
    }

    /**
     * Selects all text in the textarea.
     */
    @Method()
    async select() {
        return this.textarea.select();
    }

    private handleInput() {
        this.value = this.textarea.value;
        this.uicInput.emit();
    }

    private handleChange() {
        this.uicChange.emit();
    }

    private handleFocus() {
        this.hasFocus = true;
        this.uicFocus.emit();
    }

    private handleBlur(event: FocusEvent) {
        // only emit blur if the whole component loses focus
        if (!this.host.contains(event.relatedTarget as HTMLElement)) {
            this.hasFocus = false;
            this.uicBlur.emit();
        }
    }

    private getFlag(): string {
        if (this.invalid) {
            return "uic:flag-error";
        }

        if (this.required && !(this.value?.length > 0)) {
            return "uic:flag-mandatory";
        }

        if (this.unsaved) {
            return "uic:flag-unsaved";
        }

        return null;
    }

    render() {
        const flag = this.getFlag();
        const helperText =
            (this.invalid && this.invalidText) || this.helperText || "";
        const hasLabel = !!this.label || hasSlot(this.host, "label");
        const hasHelperText = !!helperText || hasSlot(this.host, "helper-text");

        return (
            <Host
                class={{
                    "uic-multiline-input": true,
                    "uic-multiline-input--invalid": !!this.invalid,
                    "uic-multiline-input--disabled": this.disabled,
                    "uic-multiline-input--flag": !!flag || this.hasFlagSlot,
                    "uic-multiline-input--focused": this.hasFocus,
                    "uic-multiline-input--has-label": hasLabel,
                    "uic-multiline-input--has-helper-text": hasHelperText,
                }}
            >
                <label
                    id={this.labelId}
                    htmlFor={this.inputId}
                    class="uic-multiline-input__label"
                >
                    <slot name="label">{this.label}</slot>
                </label>
                <div class="uic-multiline-input__container">
                    <textarea
                        ref={(el) => (this.textarea = el)}
                        id={this.inputId}
                        name={this.name}
                        class="uic-multiline-input__control"
                        disabled={this.disabled}
                        minLength={this.minlength}
                        maxLength={this.maxlength}
                        placeholder={this.placeholder}
                        readOnly={this.readonly}
                        value={this.value}
                        rows={this.rows}
                        onChange={() => this.handleChange()}
                        onInput={() => this.handleInput()}
                        onFocus={() => this.handleFocus()}
                        onBlur={(e) => this.handleBlur(e)}
                    ></textarea>
                    <div class="uic-multiline-input__flag">
                        <slot name="flag">
                            {flag ? (
                                <uic-icon name={flag}></uic-icon>
                            ) : (
                                <span></span>
                            )}
                        </slot>
                    </div>
                </div>
                <div class="uic-multiline-input__helper-text">
                    <slot name="helper-text">
                        <div class="uic-multiline-input__helper-text-inner">
                            {helperText}
                        </div>
                    </slot>
                </div>
            </Host>
        );
    }
}
